/**
 * One spelling for the words a person sees when creating a Session.
 *
 * Before this file, the same act had three button labels ("Create session" /
 * "Create Session" / "Create ranking") and three modal titles ("New Session" /
 * "Start Session" / "Start a session") scattered across the ~16 creation
 * surfaces — an accident of each wizard being written independently, not a
 * deliberate distinction. Every surface now imports its copy from here.
 *
 * `CREATE_RANKING_BUTTON_LABEL` is the one label kept distinct, not unified
 * into `CREATE_SESSION_BUTTON_LABEL` — and that is a canon call, not a UI
 * preference. Canon (`docs/1-canon/01-terminology-and-glossary.md`, "Session
 * Categories" and "Decision Class") names Ranking as its own Session Category
 * with its own rules ("Require the named readiness conditions for force
 * ranking", "Require explicit tie-break behavior") and `force_ranking` as its
 * own canonical Decision Class ("produces a strict total ordering"). New Rank
 * (`src/apps/rank/NewRankScreen.tsx`) is a dedicated force-ranking creation
 * surface for that category, not a reskin of generic session creation, so it
 * keeps its own verb. Every other surface creates a Session without
 * committing to that category up front, so they share the generic label.
 */

import type { SessionState } from '@/types/prioritization/session';

/** The one "create" button label for a generic session-creation surface. */
export const CREATE_SESSION_BUTTON_LABEL = 'Create session';

/** The one dialog/modal title for a generic session-creation surface. */
export const NEW_SESSION_DIALOG_TITLE = 'New session';

/**
 * The one "create" button label for a Ranking Session (force_ranking decision
 * class) creation surface. See the file header for why this is not unified
 * with `CREATE_SESSION_BUTTON_LABEL`.
 */
export const CREATE_RANKING_BUTTON_LABEL = 'Create ranking';

// ─── Session state names (canon 04 §session state machine) ─────────────────
// One name per state. Before this, sixteen screens each kept their own map and
// CRITERIA_FINALIZED read "Criteria finalized", "Criteria Set", "Set up",
// "Ready", "Ready to start", "Finalized" and "Collecting"; RUN_COMPLETE read
// "Run complete", "Run Complete", "Done", "Complete", "Completed", "Scored" and
// "Results ready". The names are canon's, in sentence case; the acts below are
// named for what the person does ("Lock criteria"); the state keeps canon 04's name, "Criteria finalized".
export const SESSION_STATE_LABELS: Record<SessionState, string> = {
  DRAFT: 'Draft',
  CRITERIA_FINALIZED: 'Criteria finalized',
  RUN_COMPLETE: 'Run complete',
  PUBLISHED: 'Published',
};

/** The one display name for a session state; an unknown value is shown as-is. */
export function sessionStateLabel(state: string | null | undefined): string {
  if (!state) return SESSION_STATE_LABELS.DRAFT;
  return SESSION_STATE_LABELS[state as SessionState] ?? state;
}

// ─── Session state acts (canon 04 §session state machine) ──────────────────
// One label per act. Before these, DRAFT → CRITERIA_FINALIZED read "Finalize
// Criteria" in one place and "Lock configuration" in another; the run act was
// "Run Prioritization" (Title Case) beside sentence-case siblings; opening a
// group decision read "Open Session for Scoring" while the other hosts said
// "Open for participants".

/**
 * DRAFT → CRITERIA_FINALIZED. The act is Lock (the Facilitator locks the Pool and
 * the criteria — owner, 2026-10-28); "finalize" is not an act word. The state
 * itself keeps canon 04's name, CRITERIA_FINALIZED, until canon renames it.
 */
export const LOCK_CRITERIA_LABEL = 'Lock criteria';

/** Opening a session (or a group decision) so participants can respond. */
export const OPEN_FOR_PARTICIPANTS_LABEL = 'Open for participants';

/** The facilitator's run: CRITERIA_FINALIZED → RUN_COMPLETE (run_session). */
export const RUN_SESSION_LABEL = 'Run prioritization';

/**
 * A participant's own input in an async session. Deliberately NOT the run
 * label: submitting one person's ranking does not run the session — the
 * facilitator's run (RUN_SESSION_LABEL) does.
 */
export const SUBMIT_MY_RANKING_LABEL = 'Submit my ranking';
export const REVISE_MY_RANKING_LABEL = 'Revise my ranking';

// ─── Corrections (migration 768, owner rulings 2026-09-22 #1 and #3) ───────

/**
 * A group-session member taking their scores back before results are revealed
 * (withdraw_group_scores). "Withdraw", not "delete": the scores are kept in
 * the reversal record; they just stop counting.
 */
export const WITHDRAW_MY_SCORES_LABEL = 'Withdraw my scores';

/**
 * The canon 01 Override stage (override_session_rank): a decision-authority
 * holder moves one item. Always shown with its reason, to everyone.
 */
export const OVERRIDE_RANK_LABEL = 'Override rank';

/** Heading for the list of overrides every reader of the results sees. */
export const RANK_OVERRIDES_HEADING = 'Rank overrides';
